import { FC, useContext } from 'react';

import { Label } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';

import s from './HostStatuses.module.scss';
import { useHostStatuses } from './useHostStatuses';

export const HostStatusesBlock: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    inService,
    on,
    busy,
    hosts,
    shell_high,
    shell_off,
    isWorker,
    loading,
  } = useHostStatuses();
  const {
    data: { permissions },
  } = useContext(AuthContext);

  if (loading) {
    return (
      <div className={cn(s.container, s.container_manager)}>
        <span>{t('hostStatuses.loader.text')}</span>
      </div>
    );
  }

  if (!hosts && permissions?.has('host_manage')) {
    return (
      <div className={cn(s.container, s.container_manager)}>
        <span>{t('hosts.none')}</span>
        <Link to="/computers" className={s.link}>
          {t('hosts.add')}
        </Link>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.block}>
        <Label
          iconName="ComputerIcon"
          color="gray-primary"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('nav.hosts')}:</span>
        <span className={cn(s.count, !hosts && s.disabled)}>
          {hosts || '-'}
        </span>
      </div>

      <div className={s.block}>
        <Label
          iconName="ShutdownIcon"
          color="green"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('hosts.statuses.on')}</span>
        <span className={cn(s.count, !hosts && s.disabled)}>
          {hosts ? on : '-'}
        </span>
      </div>

      <div className={s.block}>
        <Label
          iconName="SeansIcon"
          color="blue"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('host.statuses.session.active')}:</span>
        {!hosts ? (
          <span className={cn(s.count, s.disabled)}>-</span>
        ) : (
          <div className={s.info}>
            <div className={s.info__item}>
              <Label
                iconName="AlienIcon"
                color="blue"
                className={cn(!hosts && s.label_disabled)}
                rounded
              />
              <span className={s.count}>{busy}</span>
            </div>
            <div className={s.info__item}>
              <Label
                iconName="DashManIcon"
                color="blue"
                className={cn(!hosts && s.label_disabled)}
                rounded
              />
              <span className={s.count}>{isWorker}</span>
            </div>
          </div>
        )}
      </div>

      <div className={s.block}>
        <Label
          iconName="ConeIcon"
          color="orange"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('map.legend.service')}:</span>
        <span className={cn(s.count, !hosts && s.disabled)}>
          {hosts ? inService : '-'}
        </span>
      </div>

      <div className={s.block}>
        <Label
          iconName="TechModeIcon"
          color="red"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('host.statuses.highAccess')}:</span>
        <span className={cn(s.count, !hosts && s.disabled)}>
          {hosts ? shell_high : '-'}
        </span>
      </div>

      <div className={s.block}>
        <Label
          iconName="AlertIcon"
          color="red"
          className={cn(!hosts && s.label_disabled)}
          rounded
        />
        <span className={s.name}>{t('host.statuses.shell.off')}:</span>
        <span className={cn(s.count, !hosts && s.disabled)}>
          {hosts ? shell_off : '-'}
        </span>
      </div>
    </div>
  );
};
